import React from 'react';
import styled from 'styled-components';
import { useTranslation } from '../translations';
import { typeLabel, classLabel } from '../utils/dictionaryLabel';
import { formatMoney } from '../utils/format';
import { Modal, MEDIA_TELEFON } from './ui/Modal';
import SelectDropdown from './ui/SelectDropdown';
import { CheckIcon, SearchIcon } from '../ui/icons';
import {
  FilterField, FilterPill, LinkButton, PrimaryButton, TextInput,
} from '../ui/common';
import {
  EMPTY_FILTERS, FACET_KEYS, categoryChips, dailyPrice, facets, filterEquipment,
  hasActiveFilters, hasValue, indexEquipment, normalizeId, pruneFilters, sanitizeFilters,
  toIdSet, visibleColumns,
} from '../utils/inventoryModalFilters';

// Okno wyboru sprzętu (U.27). Jedno dla nowej rezerwacji, edycji rezerwacji, kalendarza sprzętu
// i oferty, więc props i `data-cy` są kontraktem. Reguły filtrowania: utils/inventoryModalFilters.

// Ile wierszy trafia do DOM naraz. Magazyn bywa na kilkaset sztuk, a każde zaznaczenie
// przerysowuje listę, więc kolejne porcje dochodzą dopiero, gdy przewijanie dojdzie do końca.
const CHUNK = 100;

// Trzy progi układu.
// CIASNO: telefon pionowo albo niski ekran (telefon poziomo), czyli te same warunki, w których
// wspólny Modal robi z okna pełny ekran. Pasek filtrów jest wtedy zwarty: filtry pól pod
// przyciskiem, pigułki w jednym rzędzie. Na niskim ekranie pełny pasek zajmował cały kadr.
// WASKO: do 768 px wspólny Modal układa stopkę w kolumnie i licznik wybranych idzie na całą szerokość.
// KARTY: lista przechodzi z tabeli na karty do 1000 px. Osiem kolumn tabeli nie mieści się na
// tablecie trzymanym pionowo (810–834 px): nagłówek „ROZMIAR” wychodził tam poza swoją kolumnę,
// a „Standard” łamało się w środku słowa.
const CIASNO = MEDIA_TELEFON;
const WASKO = '(max-width: 768px)';
const KARTY = '(max-width: 1000px)';

const NO_SELECTION = new Set();

const Toolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--sp-3);
`;

const SearchField = styled(FilterField)`
  flex: 1 1 320px;

  /* Na telefonie podpis znika (zostaje aria-label i tekst zastepczy), a obok pola staje
     przycisk filtrow: kazdy wiersz paska zabiera miejsce liscie. */
  @media ${CIASNO} {
    flex: 1 1 0;

    .label { display: none; }
  }
`;

// Rozmiar, marka i klasa. Na komputerze stoja w rzedzie z polem szukania; na telefonie trzy
// listy obok siebie ucinaly napis w srodku, wiec chowaja sie pod przyciskiem „Filtry”.
const Facets = styled.div`
  display: contents;

  @media ${CIASNO} {
    display: ${(p) => (p.$open ? 'grid' : 'none')};
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--sp-3);
    flex-basis: 100%;
    align-items: end;
  }
`;

const FacetsToggle = styled(FilterPill)`
  display: none;

  @media ${CIASNO} {
    display: inline-flex;
    align-items: center;
    gap: var(--sp-2);
    flex: 0 0 auto;
    border-radius: var(--r-md);
    color: var(--text);
  }
`;

const SearchBox = styled.div`
  position: relative;
  color: var(--text-muted);

  svg {
    position: absolute;
    left: var(--sp-4);
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
  }

  input {
    padding-left: calc(var(--sp-4) + 16px + var(--sp-2));
    text-overflow: ellipsis;
  }
`;

const FacetField = styled(FilterField)`
  flex: 0 1 170px;
`;

const ClearButton = styled(LinkButton)`
  align-self: center;
  white-space: nowrap;

  @media ${CIASNO} {
    justify-self: end;
  }
`;

// Pigułki kategorii: na komputerze zawijają się (najwyżej trzy rzędy, potem przewijanie), na
// telefonie stoją w jednym rzędzie przewijanym w bok, żeby nie zabierały miejsca liście.
const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-2);
  max-height: 132px;
  overflow-y: auto;

  @media ${CIASNO} {
    flex-wrap: nowrap;
    max-height: none;
    overflow-x: auto;
    overflow-y: hidden;
    margin: 0 calc(-1 * var(--sp-4));
    padding: 2px var(--sp-4);
    scrollbar-width: none;

    &::-webkit-scrollbar { display: none; }
  }
`;

const Chip = styled(FilterPill)`
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  flex: 0 0 auto;
  white-space: nowrap;
  border-color: ${(p) => (p.$active ? 'var(--primary)' : 'var(--input-border)')};
  background: ${(p) => (p.$active ? 'var(--primary)' : 'transparent')};
  color: ${(p) => (p.$active ? 'var(--primary-contrast)' : 'var(--text)')};

  &:hover:not(:disabled) {
    border-color: var(--primary);
    color: ${(p) => (p.$active ? 'var(--primary-contrast)' : 'var(--text)')};
  }
`;

const ChipCount = styled.span`
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  opacity: 0.75;
`;

const List = styled.div`
  font-size: var(--fs-base);
`;

const rowGrid = `
  display: grid;
  grid-template-columns: var(--inv-cols);
  column-gap: var(--sp-3);
  align-items: center;
  padding: 0 var(--sp-6);
`;

const HeadRow = styled.div`
  ${rowGrid}
  position: sticky;
  top: 0;
  z-index: 1;
  min-height: 40px;
  background: var(--panel-bg);
  border-bottom: 1px solid var(--border);
  font-size: var(--fs-xs);
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);

  .c-price { text-align: right; }

  @media ${KARTY} {
    display: none;
  }
`;

const Row = styled.div`
  ${rowGrid}
  min-height: 48px;
  padding-top: var(--sp-2);
  padding-bottom: var(--sp-2);
  border-bottom: 1px solid var(--border);
  cursor: pointer;
  color: var(--text);
  background: ${(p) => (p.$selected ? 'color-mix(in srgb, var(--primary) 12%, transparent)' : 'transparent')};
  transition: background var(--dur-fast) var(--ease);

  &:hover {
    background: ${(p) => (p.$selected ? 'color-mix(in srgb, var(--primary) 18%, transparent)' : 'var(--table-row-hover)')};
  }

  /* Nazwy i numery lamia sie zamiast urywac: w oknie wyboru liczy sie caly napis. */
  span { min-width: 0; overflow-wrap: anywhere; }

  .c-check { display: flex; align-items: center; }
  .c-name { font-weight: 600; }
  .c-price { text-align: right; font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .meta { display: contents; }
  [data-empty='true'] { color: var(--text-muted); }

  @media ${KARTY} {
    grid-template-columns: 32px minmax(0, 1fr) auto;
    grid-template-areas:
      'check name price'
      'check meta meta';
    row-gap: var(--sp-1);
    min-height: 60px;
    padding-top: var(--sp-3);
    padding-bottom: var(--sp-3);

    .c-check { grid-area: check; }
    .c-name { grid-area: name; }
    .c-price { grid-area: price; }
    .meta {
      grid-area: meta;
      display: flex;
      flex-wrap: wrap;
      gap: 2px var(--sp-3);
      font-size: var(--fs-sm);
      color: var(--text-muted);
    }
    /* Karta nie ma naglowka kolumn, wiec wartosc niesie swoj podpis; puste pola znikaja. */
    .meta [data-label]::before { content: attr(data-label) ': '; }
    .meta [data-empty='true'] { display: none; }
  }

  @media ${CIASNO} {
    padding-left: var(--sp-4);
    padding-right: var(--sp-4);
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  margin: 0;
  accent-color: var(--primary);
  cursor: pointer;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--sp-3);
  min-height: 220px;
  padding: var(--sp-8) var(--sp-6);
  text-align: center;
  color: var(--text-muted);
  font-weight: 600;
`;

const FooterInfo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-4);
  margin-right: auto;
  min-width: 0;

  @media ${WASKO} {
    justify-content: space-between;
    margin-right: 0;
  }
`;

const SelectedCount = styled.span`
  font-weight: 700;
  color: var(--text);
  white-space: nowrap;
`;

const DASH = '—';

const columnTemplate = (columns) => [
  '44px',
  'minmax(0, 2.4fr)',
  columns.category && 'minmax(0, 1.3fr)',
  columns.brand && 'minmax(0, 1fr)',
  columns.size && 'minmax(0, 0.8fr)',
  columns.class && 'minmax(0, 0.9fr)',
  columns.number && 'minmax(0, 1fr)',
  columns.price && '112px',
].filter(Boolean).join(' ');

const Cell = ({ value, label }) => (
  <span data-label={label} data-empty={!hasValue(value)}>
    {hasValue(value) ? value : DASH}
  </span>
);

const EquipmentRow = React.memo(function EquipmentRow({ eq, selected, columns, labels, onToggle }) {
  const price = dailyPrice(eq);
  return (
    <Row
      $selected={selected}
      onClick={() => onToggle(eq.id)}
      data-cy="inventory-row"
      data-eq-id={eq.id}
      data-selected={selected}
    >
      <span className="c-check">
        <Checkbox
          type="checkbox"
          checked={selected}
          onChange={() => onToggle(eq.id)}
          onClick={(e) => e.stopPropagation()}
          aria-label={eq.name}
          data-cy="inventory-checkbox"
        />
      </span>
      <span className="c-name">{eq.name}</span>
      <span className="meta">
        {columns.category && <Cell value={labels.type(eq.category)} />}
        {columns.brand && <Cell value={eq.brand} />}
        {columns.size && <Cell value={eq.size} label={labels.size} />}
        {columns.class && <Cell value={labels.cls(eq.class)} label={labels.class} />}
        {columns.number && <Cell value={eq.inventory_number} label={labels.number} />}
      </span>
      {columns.price && (
        <span className="c-price">{price === null ? DASH : labels.money(price)}</span>
      )}
    </Row>
  );
});

// Filtr pola: podpis i wspólna lista rozwijana panelu. Lista stoi w portalu, a o tym, że ma
// stanąć nad tym oknem, wie z kontekstu wspólnego Modal (patrz ModalLayerContext).
function FacetSelect({ name, caption, value, options, allLabel, optionLabel, onChange }) {
  const id = React.useId();
  const captionId = `${id}-caption`;
  const items = React.useMemo(
    () => [{ value: '', label: allLabel }, ...options.map((v) => ({ value: v, label: optionLabel(v) }))],
    [options, allLabel, optionLabel],
  );
  return (
    <FacetField as="div">
      <span id={captionId}>{caption}</span>
      <SelectDropdown
        id={id}
        value={value}
        onChange={onChange}
        options={items}
        disabled={options.length === 0}
        aria-labelledby={`${captionId} ${id}`}
        dataCy={`inventory-filter-${name}`}
      />
    </FacetField>
  );
}

// Najbliższy przodek, który faktycznie przewija listę. Zwykle to treść wspólnego Modal, a na
// niskim ekranie całe okno, dlatego szukamy go po stylu zamiast zakładać konkretny element.
function scrollParent(node) {
  for (let el = node ? node.parentElement : null; el; el = el.parentElement) {
    const { overflowY } = window.getComputedStyle(el);
    if (overflowY === 'auto' || overflowY === 'scroll') return el;
  }
  return null;
}

// Początek listy wraca w pole widzenia, jeśli został przewinięty ponad górną krawędź. Gdy widać
// go już (albo jest niżej, pod paskiem filtrów na niskim ekranie), przewijanie zostaje jak było.
function scrollListToTop(node) {
  const box = scrollParent(node);
  if (!node || !box) return;
  const above = node.getBoundingClientRect().top - box.getBoundingClientRect().top;
  if (above < 0) box.scrollTop += above;
}

const prefersKeyboard = () => typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

function InventoryPicker({ onClose, equipmentList, selectedIds, onToggle, title, confirmLabel }) {
  const { t, currentLanguage } = useTranslation();
  const [filters, setFilters] = React.useState(EMPTY_FILTERS);
  const [facetsOpen, setFacetsOpen] = React.useState(false);
  const [limit, setLimit] = React.useState(CHUNK);
  const sentinelRef = React.useRef(null);
  const listRef = React.useRef(null);
  const searchRef = React.useRef(null);

  // Na komputerze fokus dostaje pole szukania: pisanie i skan czytnikiem kodów trafiają od razu
  // w wyszukiwarkę. Na ekranie dotykowym fokus w polu wysuwałby klawiaturę na pół ekranu, więc
  // zostaje na samym oknie. W obu przypadkach nie stoi na „×”, które Enter by nacisnął.
  const [focusSearch] = React.useState(prefersKeyboard);

  // Rodzic podaje `onToggle` jako nową funkcję przy każdym renderze; wiersze są zapamiętane,
  // więc dostają stały uchwyt, a aktualna funkcja siedzi w ref. useLayoutEffect, nie useEffect:
  // zwykły efekt bywa spóźniony o klatkę i kliknięcie tuż po renderze wołało poprzednią wersję
  // (ten sam powód co w utils/useBarcodeScan.js).
  const onToggleRef = React.useRef(onToggle);
  React.useLayoutEffect(() => { onToggleRef.current = onToggle; }, [onToggle]);
  const handleToggle = React.useCallback((id) => {
    if (typeof onToggleRef.current === 'function') onToggleRef.current(id);
  }, []);

  // Teksty do szukania i wartości pól liczone raz na listę, nie przy każdym znaku i kliknięciu.
  const index = React.useMemo(() => indexEquipment(equipmentList), [equipmentList]);
  const selectedSet = React.useMemo(() => toIdSet(selectedIds), [selectedIds]);
  const selectedCount = selectedSet.size;
  // Zaznaczenie wpływa na listę i liczniki tylko przy „tylko wybrane”; bez tego każde kliknięcie
  // wiersza filtrowało całą listę od nowa.
  const selectionForFilters = filters.onlySelected ? selectedSet : NO_SELECTION;

  const columns = React.useMemo(() => visibleColumns(index), [index]);
  const facetState = React.useMemo(
    () => facets(index, { category: filters.category }),
    [index, filters.category],
  );
  const { total, chips } = React.useMemo(
    () => categoryChips(index, filters, selectionForFilters),
    [index, filters, selectionForFilters],
  );
  const filtered = React.useMemo(
    () => filterEquipment(index, filters, selectionForFilters),
    [index, filters, selectionForFilters],
  );

  const labels = React.useMemo(() => ({
    type: (v) => typeLabel(t, v),
    cls: (v) => classLabel(t, v),
    money: (v) => formatMoney(v, { locale: currentLanguage }),
    raw: (v) => v,
    size: t('inventory.size'),
    class: t('inventory.class'),
    number: t('inventory.inventoryNumber'),
  }), [t, currentLanguage]);

  const change = (patch) => {
    setFilters((prev) => {
      const next = { ...prev, ...patch };
      return 'category' in patch ? pruneFilters(next, index) : next;
    });
    setLimit(CHUNK);
  };

  // Lista bywa podmieniana przy otwartym oknie (świeża dostępność po skanie z telefonu): filtr
  // z wartością, której już w niej nie ma, zostawał aktywny, choć pole pokazywało „Wszystkie”.
  React.useLayoutEffect(() => {
    setFilters((prev) => sanitizeFilters(prev, index));
  }, [index]);

  // Po zmianie filtra lista zaczyna się od góry. Kontener przewijania sam nie wraca: krótsza
  // lista pokazywała swój koniec, dłuższa środek (stare okno wracało wtedy na stronę 1).
  const mounted = React.useRef(false);
  React.useLayoutEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    scrollListToTop(listRef.current);
  }, [filters]);

  const shown = filtered.slice(0, limit);
  const hasMore = filtered.length > shown.length;

  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!hasMore || !node) return undefined;
    if (typeof IntersectionObserver !== 'function') {
      setLimit(Infinity);
      return undefined;
    }
    // Obserwator jest zakładany od nowa po każdej porcji: nowy zgłasza stan od razu, więc lista
    // dociąga dalej także wtedy, gdy po dołożeniu wierszy znacznik nadal jest w kadrze.
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) setLimit((n) => n + CHUNK);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, limit]);

  const facetLabel = { size: t('inventory.size'), brand: t('inventory.brand'), class: t('inventory.class') };
  const filtersActive = hasActiveFilters(filters);
  const shownFacets = FACET_KEYS.filter((key) => facetState[key].visible);
  const activeFacets = FACET_KEYS.filter((key) => filters[key]).length;

  const toolbar = (
    <Toolbar>
      <FiltersRow>
        <SearchField>
          <span className="label">{t('common.search')}</span>
          <SearchBox>
            <SearchIcon size={16} />
            <TextInput
              ref={searchRef}
              type="text"
              inputMode="search"
              value={filters.query}
              onChange={(e) => change({ query: e.target.value })}
              placeholder={t('inventory.searchPlaceholder')}
              aria-label={t('common.search')}
              autoComplete="off"
              enterKeyHint="search"
              data-cy="inventory-search"
            />
          </SearchBox>
        </SearchField>
        {shownFacets.length > 0 && (
          <FacetsToggle
            $active={facetsOpen || activeFacets > 0}
            aria-expanded={facetsOpen}
            onClick={() => setFacetsOpen((open) => !open)}
            data-cy="inventory-filters-toggle"
          >
            {t('common.filters')}
            {activeFacets > 0 && <ChipCount>{activeFacets}</ChipCount>}
          </FacetsToggle>
        )}
        <Facets $open={facetsOpen}>
          {shownFacets.map((key) => (
            <FacetSelect
              key={key}
              name={key}
              caption={facetLabel[key]}
              value={filters[key]}
              options={facetState[key].options}
              allLabel={t('common.all')}
              optionLabel={key === 'class' ? labels.cls : labels.raw}
              onChange={(value) => change({ [key]: value })}
            />
          ))}
          {filtersActive && (
            <ClearButton onClick={() => change(EMPTY_FILTERS)} data-cy="inventory-clear-filters">
              {t('common.clearFilters')}
            </ClearButton>
          )}
        </Facets>
      </FiltersRow>

      {chips.length > 0 && (
        <Chips role="group" aria-label={t('inventory.category')}>
          <Chip
            $active={!filters.category}
            onClick={() => change({ category: '' })}
            data-cy="inventory-category-all"
          >
            {t('common.all')}
            <ChipCount data-cy="inventory-category-count">{total}</ChipCount>
          </Chip>
          {chips.map((chip) => {
            const active = filters.category === chip.value;
            return (
              <Chip
                key={chip.value}
                $active={active}
                disabled={chip.count === 0 && !active}
                onClick={() => change({ category: active ? '' : chip.value })}
                data-cy="inventory-category-chip"
                data-category={chip.value}
              >
                {labels.type(chip.value)}
                <ChipCount data-cy="inventory-category-count">{chip.count}</ChipCount>
              </Chip>
            );
          })}
        </Chips>
      )}
    </Toolbar>
  );

  const footer = (
    <>
      <FooterInfo>
        <SelectedCount aria-live="polite" data-cy="inventory-selected-count">
          {t('inventoryModal.selectedCount', { count: selectedCount })}
        </SelectedCount>
        <FilterPill
          $active={filters.onlySelected}
          disabled={selectedCount === 0 && !filters.onlySelected}
          onClick={() => change({ onlySelected: !filters.onlySelected })}
          data-cy="inventory-only-selected"
        >
          {t('inventoryModal.onlySelected')}
        </FilterPill>
      </FooterInfo>
      <PrimaryButton type="button" onClick={onClose} data-cy="inventory-confirm">
        <CheckIcon size={16} />
        {confirmLabel || (selectedCount > 0
          ? t('inventoryModal.confirm', { count: selectedCount })
          : t('common.close'))}
      </PrimaryButton>
    </>
  );

  let emptyText = null;
  if (index.length === 0) emptyText = t('inventoryModal.empty');
  else if (filtered.length === 0) {
    emptyText = filters.onlySelected && selectedCount === 0
      ? t('inventoryModal.noneSelected')
      : t('inventory.noMatch');
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={title || t('reservation.chooseEquipment')}
      closeLabel={t('common.close')}
      size="xl"
      tall
      flush
      fullScreenOnMobile
      elevated /* ponad nakładkami formularzy, z których okno jest otwierane (kalendarz, oferta) */
      initialFocus={focusSearch ? searchRef : 'dialog'}
      toolbar={toolbar}
      footer={footer}
      data-cy="inventory-modal"
    >
      <List ref={listRef} style={{ '--inv-cols': columnTemplate(columns) }}>
        {emptyText ? (
          <EmptyState data-cy="inventory-empty">
            <span>{emptyText}</span>
            {index.length > 0 && filtersActive && (
              <LinkButton onClick={() => change(EMPTY_FILTERS)} data-cy="inventory-empty-clear">
                {t('common.clearFilters')}
              </LinkButton>
            )}
          </EmptyState>
        ) : (
          <>
            <HeadRow>
              <span />
              <span>{t('inventory.name')}</span>
              {columns.category && <span>{t('inventory.category')}</span>}
              {columns.brand && <span>{t('inventory.brand')}</span>}
              {columns.size && <span>{t('inventory.size')}</span>}
              {columns.class && <span>{t('inventory.class')}</span>}
              {columns.number && <span>{t('inventory.inventoryNumber')}</span>}
              {columns.price && <span className="c-price">{t('inventoryModal.pricePerDay')}</span>}
            </HeadRow>
            {shown.map((eq) => (
              <EquipmentRow
                key={eq.id}
                eq={eq}
                selected={selectedSet.has(normalizeId(eq.id))}
                columns={columns}
                labels={labels}
                onToggle={handleToggle}
              />
            ))}
            {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
          </>
        )}
      </List>
    </Modal>
  );
}

// Dawny prop `itemsPerPage` nie jest już czytany: lista jest przewijana, nie stronicowana.
export default function InventoryModal({
  isOpen,
  onClose,
  equipmentList = [],
  selectedIds = [],
  onToggle,
  title,
  confirmLabel,
}) {
  // Wnętrze montuje się dopiero przy otwarciu, więc filtry zaczynają każdorazowo od zera.
  if (!isOpen) return null;
  return (
    <InventoryPicker
      onClose={onClose}
      equipmentList={equipmentList}
      selectedIds={selectedIds}
      onToggle={onToggle}
      title={title}
      confirmLabel={confirmLabel}
    />
  );
}
